import React, { useCallback, useEffect, useRef, useState } from "react"
import { AddOfferHelperText, ChangeLanguageComponent } from "components/shared/ModalHelpers"
import RichTextEditor from "components/shared/RichTextEditor/RichTextEditor"
import { Controller, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { LocalStorageService, ProfileSurveyService } from "services/api-service"
import { ShortSurveyType } from "services/api-service/types"
import { SentryService } from "services/SentryService"
import { logout } from "store/auth"
import { RootState } from "store/store"
import styled from "styled-components"
import { Box, Row } from "ui/components/atoms/_atoms"
import Button from "ui/components/atoms/button"
import Checkbox from "ui/components/atoms/checkbox"
import CheckboxesGroup from "ui/components/atoms/checkboxes-group"
import Input, { InputAutocomplete } from "ui/components/atoms/input"
import Select from "ui/components/atoms/select"
import Switch from "ui/components/atoms/switch"
import { H3, H4, P } from "ui/components/atoms/typography"
import Modal from "ui/components/molecules/modal"
import {
  CAST_ARRAY,
  CURRENCY_ARRAY,
  DIRECTIONS_ARRAY,
  DRIVINGAREAS_ARRAY,
  EXPERIENCE_ARRAY,
  LANGUAGES_ARRAY,
  PARAMS_OTHERS_ARRAY,
  translate,
  TRUCKTYPES_ARRAY,
  WORKLOCATION_ARRAY,
  WORKTYPES_ARRAY,
} from "ui/data"
import { PARAMS_DRIVER_ARRAY, WORKTYPES_DRIVER_ARRAY } from "ui/data/OfferInformations"
import { WEBSITE_REGEX } from "ui/utils/regex"

const AddOfferAdditioanlInfo = ({ state, onBack, onChange }: any) => {
  const dispatch = useDispatch()
  const { t, i18n } = useTranslation(["add-offer", "common", "data"])
  const { profile } = useSelector((state: RootState) => state.profile)
  const legalName = profile?.type === "company" ? profile?.company?.name : profile?.name
  const legalCity = profile?.type === "company" ? profile?.company?.city : profile?.address.city
  const legalAddress =
    profile?.type === "company" ? profile?.company?.address : profile?.address.address

  const timeoutId = useRef<NodeJS.Timeout | null>(null)
  const [loadingSurvey, setLoadingSurvey] = useState<boolean>(false)
  const [surveysOptions, setSurveyOptions] = useState<ShortSurveyType[]>([])

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    setError,
    reset,
    control,
    unregister,
    formState: { errors, isValid },
  } = useForm({
    shouldFocusError: true,
    defaultValues: {
      description1: state.description1 || "",
      description2: state.description2 || "",
      description3: state.description3 || "",
      legalInformationClause:
        state.legalInformationClause ||
        t("additional-info.legal.info.defaultValue", {
          ns: "add-offer",
          legalName: legalName,
          legalCity,
          legalAddress,
        }),
      legalFutureRecruitment:
        state.legalFutureRecruitment ||
        t("additional-info.legal.future-apply.defaultValue", {
          ns: "add-offer",
        }),
      applyLink: state.applyLink || "",
      survey: state.survey,
    },
  })

  const [drivingAreas, setDrivingAreas] = useState(
    state.drivingAreas || state.category === "drivers" ? ["national", "international"] : [],
  )
  const [workTypes, setWorkTypes] = useState(
    state.workTypes || state.category === "drivers"
      ? ["2_1", "2_2", "3_1", "4_1", "4_2", "6_2", "8_4"]
      : ["fulltime"],
  )
  const [truckTypes, setTruckTypes] = useState(state.truckTypes)
  const [directions, setDirections] = useState(state.directions)

  const [workLocations, setWorkLocations] = useState(
    state.workLocations || state.category === "drivers" ? [] : ["office"],
  )
  const [languages, setLanguages] = useState(state.languages || ["polish"])
  const [cast, setCast] = useState(state.cast || [])
  const [experience, setExperience] = useState(state.experience ?? ["exp_no"])
  const [params, setParams] = useState(
    state.uaFriendly ? [...state.params, "uaFriendly"] : state.params,
  )
  const [switches, setSwitches] = useState({
    legalInformationClause: true,
    legalFutureRecruitment: state.switches?.legalFutureRecruitment ?? false,
  })
  const [descriptions, setDescriptions] = useState({
    description1: state.descriptions?.description1 || "",
    description2: state.descriptions?.description2 || "",
    description3: state.descriptions?.description3 || "",
  })

  const [descriptionErrors, setDescriptionErrors] = useState({
    description1: "",
    description2: "",
    description3: "",
  })

  const [surveyInput, setSurveyInput] = useState<string | undefined>(state?.survey?.label || "")

  // Helper function to strip HTML and count characters
  const getTextLength = useCallback((html: string): number => {
    if (!html) return 0
    const tempDiv = document.createElement("div")
    tempDiv.innerHTML = html
    return tempDiv.textContent?.trim().length || 0
  }, [])

  const onChangeSwitches = ({ name, value }: { name: string; value: boolean }) => {
    setSwitches({
      ...switches,
      [name]: value,
    })
  }

  const onSubmit = useCallback(
    async (el: any) => {
      // Validate descriptions before submitting
      const errors = {
        description1: "",
        description2: "",
        description3: "",
      }

      let hasErrors = false

      if (getTextLength(descriptions.description1) < 50) {
        errors.description1 = t("additional-info.error.description-min-length", { ns: "add-offer" })
        hasErrors = true
      }

      if (getTextLength(descriptions.description2) < 50) {
        errors.description2 = t("additional-info.error.description-min-length", { ns: "add-offer" })
        hasErrors = true
      }

      if (getTextLength(descriptions.description3) < 50) {
        errors.description3 = t("additional-info.error.description-min-length", { ns: "add-offer" })
        hasErrors = true
      }

      setDescriptionErrors(errors)

      if (hasErrors) {
        return
      }

      onChange({
        ...el,
        switches,
        drivingAreas,
        workTypes,
        workLocations,
        truckTypes,
        directions,
        languages,
        descriptions,
        experience,
        cast,
        params,
      })
    },
    [
      switches,
      onChange,
      drivingAreas,
      workTypes,
      workLocations,
      truckTypes,
      directions,
      languages,
      descriptions,
      experience,
      cast,
      params,
      getTextLength,
      t,
    ],
  )

  const handleOnChangeRichTextEditor = useCallback(
    ({ target }: { target: { name: string; value: string } }) => {
      setDescriptions({
        ...descriptions,
        [target.name]: target.value,
      })

      // Clear error when user starts typing
      if (descriptionErrors[target.name as keyof typeof descriptionErrors]) {
        setDescriptionErrors({
          ...descriptionErrors,
          [target.name]: "",
        })
      }
    },
    [descriptions, descriptionErrors],
  )

  const searchSurveyAutocomplete = useCallback(async (val: string) => {
    try {
      setLoadingSurvey(true)
      const { content } = await ProfileSurveyService.searchSurveys(val)
      setSurveyOptions(content)
    } catch (e) {
      SentryService.error("[ERROR ProfileSurveyService.searchSurveys]:", e)
    } finally {
      setLoadingSurvey(false)
    }
  }, [])

  useEffect(() => {
    if (timeoutId.current) {
      clearTimeout(timeoutId.current)
    }

    timeoutId.current = setTimeout(() => {
      if (surveyInput) {
        searchSurveyAutocomplete(surveyInput)
      }
    }, 1000)
  }, [surveyInput, searchSurveyAutocomplete])

  const handleLogOut = useCallback(() => {
    LocalStorageService.removeObject("token")
    dispatch(logout())
  }, [dispatch])

  return (
    <Modal
      show={true}
      icon="plus-circle"
      title={t("additional-info.title")}
      text={t("additional-info.text")}
      $maxWidth="900px"
      showBackground={false}
      mobileFullScreen={true}
      helperElement={
        <ChangeLanguageComponent>
          <AddOfferHelperText />
        </ChangeLanguageComponent>
      }
      helperButton={{
        text: t("logout", { ns: "common" }),
        onClick: handleLogOut,
      }}
      bottomElement={
        <Row $justifyContent="flex-end">
          <Button $type="secondary" onClick={onBack} $marginRight="S">
            {t("back", { ns: "common" })}
          </Button>
          <Button onClick={handleSubmit(onSubmit)}>{t("next", { ns: "common" })}</Button>
        </Row>
      }
    >
      <>
        <BasicInfoGrid>
          <P label={t("offer.name", { ns: "form" })} $marginBottom="0">
            {state.name}
          </P>
          <P label={t("offer.category", { ns: "form" })} $marginBottom="0">
            {`${t(state.category, { ns: "data" })}, ${t(state.subcategory, { ns: "data" })}`}
          </P>
          <P label={t("address", { ns: "form" })} $marginBottom="0">
            {state.address?.name}
          </P>
        </BasicInfoGrid>
        <BlockWrapper>
          <H3 $marginBottom="0">{t("additional-info.more-info.title", { ns: "add-offer" })}</H3>
          <Box>
            <Box $marginBottom="S">
              <H4 $marginBottom="XXS">
                {t("additional-info.more-info.responsibilities.title", { ns: "add-offer" })}
              </H4>
              <P $small $marginBottom="0">
                {t("additional-info.more-info.responsibilities.text", { ns: "add-offer" })}
              </P>
            </Box>
            <RichTextEditor
              name="description1"
              value={descriptions.description1}
              onChange={handleOnChangeRichTextEditor}
              $marginTop="S"
            />
            {descriptionErrors.description1 && (
              <P $small $marginTop="XS" style={{ color: "#d32f2f" }}>
                {descriptionErrors.description1}
              </P>
            )}
          </Box>
          <Box>
            <Box $marginBottom="S">
              <H4 $marginBottom="XXS">
                {t("additional-info.more-info.requirements.title", { ns: "add-offer" })}
              </H4>
              <P $small $marginBottom="0">
                {t("additional-info.more-info.requirements.text", { ns: "add-offer" })}
              </P>
            </Box>
            <RichTextEditor
              name="description2"
              value={descriptions.description2}
              onChange={handleOnChangeRichTextEditor}
              $marginTop="S"
            />
            {descriptionErrors.description2 && (
              <P $small $marginTop="XS" style={{ color: "#d32f2f" }}>
                {descriptionErrors.description2}
              </P>
            )}
          </Box>
          <Box>
            <Box $marginBottom="S">
              <H4 $marginBottom="XXS">
                {t("additional-info.more-info.we-offer.title", { ns: "add-offer" })}
              </H4>
              <P $small $marginBottom="0">
                {t("additional-info.more-info.we-offer.text", { ns: "add-offer" })}
              </P>
            </Box>
            <RichTextEditor
              name="description3"
              value={descriptions.description3}
              onChange={handleOnChangeRichTextEditor}
              $marginTop="S"
            />
            {descriptionErrors.description3 && (
              <P $small $marginTop="XS" style={{ color: "#d32f2f" }}>
                {descriptionErrors.description3}
              </P>
            )}
          </Box>
        </BlockWrapper>
        <BlockWrapper>
          <H3 $marginBottom="0">{t("additional-info.params.title", { ns: "add-offer" })}</H3>
          {state.category === "drivers" && (
            <>
              <Box>
                <H4 $marginBottom="XXS">
                  {t("additional-info.params.work-type", { ns: "add-offer" })}
                </H4>
                <CheckboxesGroup
                  name="drivingAreas"
                  $type="secondary"
                  options={DRIVINGAREAS_ARRAY.map(el => [t(el[1], { ns: "data" }), el[1]])}
                  value={drivingAreas}
                  onChange={({ value }) => setDrivingAreas(value)}
                />
              </Box>
              <Box>
                <H4 $marginBottom="XXS">
                  {t("additional-info.params.truck-type", { ns: "add-offer" })}
                </H4>
                <CheckboxesGroup
                  name="truckTypes"
                  $type="secondary"
                  options={TRUCKTYPES_ARRAY.map(el => [t(el[1], { ns: "data" }), el[1]])}
                  value={truckTypes}
                  onChange={({ value }) => setTruckTypes(value)}
                />
              </Box>
              <Box>
                <H4 $marginBottom="XXS">{t("additional-info.params.cast", { ns: "add-offer" })}</H4>
                <CheckboxesGroup
                  name="workLocations"
                  $type="secondary"
                  options={CAST_ARRAY.map(el => [t(el[1], { ns: "data" }), el[1]])}
                  value={cast}
                  onChange={({ value }) => {
                    if (value.length === 0) {
                      setExperience(["single_cast"])
                      return
                    }
                    setCast(value)
                  }}
                />
              </Box>
              <Box>
                <H4 $marginBottom="XXS">
                  {t("additional-info.params.directions", { ns: "add-offer" })}
                </H4>
                <CheckboxesGroup
                  name="directions"
                  $type="secondary"
                  options={DIRECTIONS_ARRAY.map(el => [t(el[1], { ns: "data" }), el[1]])}
                  value={directions}
                  onChange={({ value }) => setDirections(value)}
                />
              </Box>
            </>
          )}
          {state.category === "others" && (
            <>
              <Box>
                <H4 $marginBottom="XXS">
                  {t("additional-info.params.work-type", { ns: "add-offer" })}
                </H4>
                <CheckboxesGroup
                  name="workTypes"
                  $type="secondary"
                  options={WORKTYPES_ARRAY.map(el => [t(el[1], { ns: "data" }), el[1]])}
                  value={workTypes}
                  onChange={({ value }) => setWorkTypes(value)}
                />
              </Box>
              <Box>
                <H4 $marginBottom="XXS">
                  {t("additional-info.params.work-mode", { ns: "add-offer" })}
                </H4>
                <CheckboxesGroup
                  name="workLocations"
                  $type="secondary"
                  options={WORKLOCATION_ARRAY.map(el => [t(el[1], { ns: "data" }), el[1]])}
                  value={workLocations}
                  onChange={({ value }) => setWorkLocations(value)}
                />
              </Box>
            </>
          )}
          <Box>
            <H4 $marginBottom="XXS">
              {t("additional-info.params.experience", { ns: "add-offer" })}
            </H4>
            <CheckboxesGroup
              name="workLocations"
              $type="secondary"
              options={EXPERIENCE_ARRAY.map(el => [t(el[1], { ns: "data" }), el[1]])}
              value={experience}
              onChange={({ value }) => {
                if (value.length === 0) {
                  setExperience(["exp_no"])
                  return
                }
                setExperience([value.at(-1)])
              }}
            />
          </Box>
          <Box>
            <H4 $marginBottom="XXS">
              {t("additional-info.params.languages", { ns: "add-offer" })}
            </H4>
            <CheckboxesGroup
              name="languages"
              $type="secondary"
              options={LANGUAGES_ARRAY.map(el => [t(el[1], { ns: "data" }), el[1]])}
              value={languages}
              onChange={({ value }) => setLanguages(value)}
            />
          </Box>
          <Box>
            <H4 $marginBottom="XXS">{t("additional-info.params.others", { ns: "add-offer" })}</H4>
            <CheckboxesGroup
              name="drivingAreas"
              $type="secondary"
              options={
                state.category === "drivers"
                  ? PARAMS_DRIVER_ARRAY.map(el => [t(el[1], { ns: "data" }), el[1]])
                  : PARAMS_OTHERS_ARRAY.map(el => [t(el[1], { ns: "data" }), el[1]])
              }
              value={params}
              onChange={({ value }) => setParams(value)}
            />
          </Box>
        </BlockWrapper>
        {state.plan === "premium" && (
          <BlockWrapper>
            <Box>
              <H3 $marginBottom="XXS">{t("additional-info.apply.title", { ns: "add-offer" })}</H3>
              <P $small $marginBottom="0">
                {t("additional-info.apply.text", { ns: "add-offer" })}
              </P>
            </Box>
            <Box>
              <Input
                name="applyLink"
                value={watch("applyLink")}
                error={errors.applyLink}
                rhf={register("applyLink", {
                  required: false,
                  pattern: {
                    value: WEBSITE_REGEX,
                    message: t("wrong-website", { ns: "form" }),
                  },
                })}
                startAdornment="https://"
              />
            </Box>
          </BlockWrapper>
        )}
        {state.plan === "premium" && (
          <BlockWrapper>
            <Box>
              <H3 $marginBottom="XXS">{t("additional-info.survey.title", { ns: "add-offer" })}</H3>
              <P $small $marginBottom="0">
                {t("additional-info.survey.text", { ns: "add-offer" })}
              </P>
            </Box>
            <Box>
              <Controller
                name="survey"
                control={control}
                render={({ field }) => (
                  <InputAutocomplete
                    label={`${t("surveyName", { ns: "form" })}`}
                    error={errors.survey}
                    inputValue={surveyInput}
                    onInputChange={setSurveyInput}
                    noOptionsText={t("not-found", { ns: "form" })}
                    loading={loadingSurvey}
                    options={surveysOptions.map(el => {
                      return {
                        id: `${el.id}`,
                        label: el.name,
                        ...el,
                      }
                    })}
                    {...field}
                  />
                )}
              />
            </Box>
          </BlockWrapper>
        )}
        <BlockWrapper className="hide_border">
          <H3 $marginBottom="0">{t("additional-info.legal.title", { ns: "add-offer" })}</H3>
          <Box>
            <Row>
              <Switch
                disabled
                name="legalInformationClause"
                value={switches.legalInformationClause}
                onChange={onChangeSwitches}
              />
              <Box $marginLeft="S">
                <H4 $marginBottom="XXS">
                  {t("additional-info.legal.info.title", { ns: "add-offer" })}*
                </H4>
                <P $small $marginBottom="0">
                  {t("additional-info.legal.info.text", { ns: "add-offer" })}
                </P>
              </Box>
            </Row>
            <Input
              name="legalInformationClause"
              value={watch("legalInformationClause")}
              error={errors.legalInformationClause}
              multiline
              rows={12}
              rhf={register("legalInformationClause", {
                required: t("required", { ns: "form" }),
              })}
              $marginTop="S"
            />
          </Box>
          <Box>
            <Row>
              <Switch
                name="legalFutureRecruitment"
                value={switches.legalFutureRecruitment}
                onChange={onChangeSwitches}
              />
              <Box $marginLeft="S">
                <H4 $marginBottom="XXS">
                  {t("additional-info.legal.future-apply.title", { ns: "add-offer" })}*
                </H4>
                <P $small $marginBottom="0">
                  {t("additional-info.legal.future-apply.text", { ns: "add-offer" })}
                </P>
              </Box>
            </Row>
            {switches.legalFutureRecruitment && (
              <Input
                name="legalFutureRecruitment"
                value={watch("legalFutureRecruitment")}
                error={errors.legalFutureRecruitment}
                multiline
                rows={6}
                rhf={register("legalFutureRecruitment", {
                  required: t("required", { ns: "form" }),
                })}
                $marginTop="S"
              />
            )}
          </Box>
        </BlockWrapper>
      </>
    </Modal>
  )
}

export default AddOfferAdditioanlInfo

const BlockWrapper = styled(Box)`
  padding: ${({ theme }) => theme.space.M} ${({ theme }) => theme.space.S};
  width: calc(100% + (${({ theme }) => theme.space.S} * 2));
  margin-left: -${({ theme }) => theme.space.S};
  border-bottom: 1px solid ${({ theme }) => theme.color.border};
  display: flex;
  flex-direction: column;
  grid-gap: ${({ theme }) => theme.space.S};

  &.hide_border {
    border-bottom: none;
  }
`

const BasicInfoGrid = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  grid-gap: ${({ theme }) => theme.space.L};
  grid-row-gap: ${({ theme }) => theme.space.S};
  margin-bottom: ${({ theme }) => theme.space.S};
`
